import { h } from '@/components/dom';
import { button, iconButton, setButtonContent } from '@/components/controls';
import { icons, svgIcon } from '@/components/icons';
import type { SaveState } from '@/state/appState';
import type { App } from './App';
import { createInputPanel } from './InputPanel';
import { createTargetPanel } from './TargetPanel';
import { createPinsPanel } from './PinsPanel';
import { createAlgorithmPanel } from './AlgorithmPanel';
import { createRenderPanel } from './RenderPanel';
import { createExportPanel } from './ExportPanel';
import { createEditPanel } from './EditPanel';
import { createTimeline } from './TimelinePanel';
import { createDebugPanel } from './DebugPanel';
import { createStatusBar } from './StatusBar';
import { createStageChrome } from './StageToolbar';

export interface Layout {
  element: HTMLElement;
  stage: HTMLElement;
}

const TABS: Array<{ id: string; label: string; icon: string; build: (app: App) => HTMLElement }> = [
  { id: 'input', label: 'Input', icon: icons.image, build: createInputPanel },
  { id: 'target', label: 'Target', icon: icons.sliders, build: createTargetPanel },
  { id: 'pins', label: 'Pins', icon: icons.pins, build: createPinsPanel },
  { id: 'algorithm', label: 'Algorithm', icon: icons.wand, build: createAlgorithmPanel },
  { id: 'render', label: 'Render', icon: icons.brush, build: createRenderPanel },
  { id: 'edit', label: 'Edit', icon: icons.edit, build: createEditPanel },
  { id: 'export', label: 'Export', icon: icons.exportBox, build: createExportPanel },
];

const SAVE_WORD: Record<SaveState, string> = { none: '', saving: 'Saving…', saved: 'Saved', error: 'Not saved' };

export function buildLayout(app: App): Layout {
  // ---- top bar
  const generateBtn = button({ label: 'Generate', variant: 'primary', icon: icons.play, title: 'Start / pause generation (G)', onClick: () => void app.generate() });
  const stepBtn = iconButton({ icon: icons.step, title: 'Add one string', onClick: () => void app.stepOnce() });
  const cancelBtn = iconButton({ icon: icons.stop, title: 'Cancel the running job', onClick: () => void app.cancel() });
  const resetBtn = iconButton({ icon: icons.trash, title: 'Remove all strings', danger: true, onClick: () => void app.resetSequence() });
  const undoBtn = iconButton({ icon: icons.undo, title: 'Undo (Ctrl+Z)', onClick: () => void app.undo() });
  const redoBtn = iconButton({ icon: icons.redo, title: 'Redo (Ctrl+Y)', onClick: () => void app.redo() });
  const saveBtn = button({ label: 'Save', icon: icons.download, title: 'Save the project as JSON', onClick: () => void app.saveProject() });
  const openInput = h('input', { type: 'file', accept: '.json,application/json', style: { display: 'none' } });
  openInput.addEventListener('change', () => {
    const f = openInput.files?.[0];
    if (f) void app.openProjectFile(f);
    openInput.value = '';
  });
  const openBtn = button({ label: 'Open', icon: icons.folder, title: 'Open a project file', onClick: () => openInput.click() });
  const debugBtn = iconButton({ icon: icons.gauge, title: 'Developer panel', className: 'ed__debug', onClick: () => app.updateUi({ debugOpen: !app.state.ui.debugOpen }) });

  const name = h('input', { class: 'ed__name', type: 'text', spellcheck: false, placeholder: 'Untitled design', attrs: { 'aria-label': 'Design name' } });
  name.addEventListener('change', () => app.renameDesign(name.value));
  name.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') app.renameDesign(name.value);
    else if (e.key === 'Escape') name.value = app.state.design.name;
    else return;
    e.preventDefault();
    name.blur();
  });
  const saved = h('span', { class: 'ed__saved', hidden: true });
  const header = h(
    'header',
    { class: 'ed__top' },
    h('a', { class: 'ed__brand', href: '/', title: 'All designs' }, svgIcon(icons.back), h('span', null, 'String Canvas')),
    name,
    saved,
    h('div', { class: 'ed__mid' }, undoBtn, redoBtn, h('i', { class: 'ed__sep' }), stepBtn, cancelBtn, resetBtn),
    h('div', { class: 'ed__right' }, openBtn, saveBtn, openInput, h('i', { class: 'ed__sep' }), debugBtn, generateBtn),
  );

  // ---- side tabs and the panel they open
  const tabBar = h('nav', { class: 'sidetabs', attrs: { role: 'tablist', 'aria-label': 'Panels' } });
  const pane = h('div', { class: 'pane' });
  const panels = new Map<string, HTMLElement>();
  const tabButtons: HTMLButtonElement[] = [];
  for (const tab of TABS) {
    const btn = h('button', { type: 'button', class: 'sidetab', dataset: { tab: tab.id }, title: tab.label, attrs: { role: 'tab', 'aria-selected': 'false' } }, svgIcon(tab.icon), h('span', null, tab.label));
    btn.addEventListener('click', () => app.updateUi({ activePanel: tab.id }));
    tabBar.appendChild(btn);
    tabButtons.push(btn);
    const panel = tab.build(app);
    panel.classList.add('panel');
    panel.dataset.panel = tab.id;
    panel.setAttribute('role', 'tabpanel');
    panels.set(tab.id, panel);
    pane.appendChild(panel);
  }
  const sidebar = h('aside', { class: 'ed__left' }, pane);

  // ---- workspace: the artboard, a floating tool rail, the view switcher and an empty-frame hint
  const stage = h('div', { class: 'stage', attrs: { 'aria-label': 'Main canvas' } });
  const chrome = createStageChrome(app, stage);
  const hint = h(
    'div',
    { class: 'ed__hint' },
    h('b', null, 'An empty frame'),
    h('span', null, 'Drop a photo anywhere, paste one, or pick a sample under ', h('em', null, 'Input'), '. Then press ', h('kbd', null, 'G'), ' to generate.'),
  );
  const work = h('main', { class: 'ed__work' }, stage, chrome.rail, chrome.viewbar, hint);
  const debug = createDebugPanel(app);
  const timeline = createTimeline(app);
  const status = createStatusBar(app);
  const element = h('div', { class: 'ed' }, header, h('div', { class: 'ed__body' }, tabBar, sidebar, work, debug), timeline, status);

  app.store.subscribe((state, previous) => {
    if (state.ui.activePanel !== previous.ui.activePanel || state === previous) {
      for (const [id, panel] of panels) panel.hidden = id !== state.ui.activePanel;
      for (const b of tabButtons) {
        const on = b.dataset.tab === state.ui.activePanel;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-selected', String(on));
      }
    }
    if (state.source !== previous.source || state === previous) {
      const has = state.source.kind === 'image';
      name.title = has ? `${state.source.name}, ${state.source.width}×${state.source.height}` : '';
    }
    if (state.design !== previous.design || state === previous) {
      if (document.activeElement !== name) name.value = state.design.name;
      document.title = state.design.name ? `${state.design.name} – String Canvas` : 'String Canvas';
      const word = SAVE_WORD[state.design.save];
      saved.hidden = !word;
      saved.textContent = word;
      saved.dataset.status = state.design.save;
    }
    if (state.target.width !== previous.target.width || state === previous) hint.hidden = state.target.width > 0;
    if (state.status !== previous.status || state === previous || state.history !== previous.history || state.target.width !== previous.target.width || state.ui.debugOpen !== previous.ui.debugOpen || state.solution.pins !== previous.solution.pins) {
      const running = state.status === 'running';
      const hasTarget = state.target.width > 0;
      const busy = state.status === 'loading' || state.status === 'preparing' || state.status === 'booting';
      setButtonContent(generateBtn, running ? 'Pause' : state.status === 'paused' ? 'Resume' : 'Generate', running ? icons.pause : icons.play);
      generateBtn.disabled = !hasTarget || busy;
      stepBtn.disabled = !hasTarget || busy || running;
      cancelBtn.disabled = !running;
      resetBtn.disabled = !hasTarget || busy || running;
      undoBtn.disabled = !state.history.canUndo || running;
      redoBtn.disabled = !state.history.canRedo || running;
      undoBtn.title = state.history.undoLabel ? `Undo: ${state.history.undoLabel}` : 'Undo (Ctrl+Z)';
      redoBtn.title = state.history.redoLabel ? `Redo: ${state.history.redoLabel}` : 'Redo (Ctrl+Y)';
      saveBtn.disabled = state.solution.pins.length === 0;
      debugBtn.classList.toggle('is-on', state.ui.debugOpen);
    }
  }, { immediate: true });

  return { element, stage };
}
